import type {
  OperationProgressModel,
  OperationProgressStepDefinition,
} from '../../../shared/ui/progress';
import {
  buildOrderedProgressSteps,
  clampProgressCount,
  clampProgressPercent,
  isOperationProgressWaitingState,
} from '../../../shared/ui/progress';
import type {
  QuickFillSyncProgress,
  QuickFillSyncProgressPhase,
} from '../../../shared/services/quickFillRevitService';
import {
  formatQuickFillLoadStep,
  getQuickFillLoadPercent,
  type QuickFillLoadProgress,
} from './quickFillLoadProgress';

const applySteps: Array<OperationProgressStepDefinition<QuickFillSyncProgressPhase>> = [
  {
    id: 'preparing',
    label: 'Prepare payload',
    detail: 'Detect changed cells and group Revit writes',
  },
  {
    id: 'writing-project-types',
    label: 'Project type values',
    detail: 'Apply project-visible type parameters in batch',
  },
  {
    id: 'writing-elements',
    label: 'Project instance values',
    detail: 'Apply project-visible instance parameters in batch',
  },
  {
    id: 'completed',
    label: 'Confirm result',
    detail: 'Map Revit response back to table rows',
  },
];

const applyPhaseOrder = applySteps.map((step) => step.id);

export function buildQuickFillApplyProgressModel(
  progress: QuickFillSyncProgress | null | undefined
): OperationProgressModel {
  const phase = progress?.phase ?? 'preparing';
  const waiting = isOperationProgressWaitingState(progress?.state);
  const currentStepIndex = Math.max(0, applyPhaseOrder.indexOf(phase));
  const total = Math.max(0, progress?.total ?? 0);
  const current = clampProgressCount(progress?.current, total);
  const explicitPercent = clampProgressPercent(progress?.percent);
  const stepLabel = `Step ${Math.min(currentStepIndex + 1, applySteps.length)} of ${applySteps.length}`;

  return {
    title: 'Applying Quick Fill changes',
    message: progress?.message || 'Preparing Quick Fill changes for Revit',
    detail: waiting && progress?.waitReason
      ? progress.waitReason
      : total > 0
      ? `${current} of ${total} values confirmed`
      : progress?.detail || 'Waiting for Revit to accept the write request',
    status: waiting ? 'waiting' : phase === 'completed' ? 'complete' : 'running',
    operationId: progress?.operationId,
    phase,
    state: progress?.state,
    waitReason: progress?.waitReason,
    waitElapsedMs: progress?.waitElapsedMs,
    activeElapsedMs: progress?.activeElapsedMs,
    currentItem: progress?.currentItem,
    processed: current,
    total,
    percent: explicitPercent,
    elapsedMs: progress?.elapsedMs,
    etaMs: waiting ? undefined : progress?.etaMs,
    cacheStatus: progress?.cacheStatus,
    resultReady: progress?.resultReady,
    stepLabel,
    locked: true,
    steps: buildOrderedProgressSteps(applySteps, currentStepIndex, {
      complete: phase === 'completed',
    }),
    metrics: total > 0
      ? [
          { label: 'Confirmed', value: String(current), tone: 'success' },
          { label: 'Total values', value: String(total) },
        ]
      : undefined,
  };
}

export function buildQuickFillLoadProgressModel(
  progress: QuickFillLoadProgress | null | undefined
): OperationProgressModel {
  const percent = getQuickFillLoadPercent(progress);
  const stepLabel = formatQuickFillLoadStep(progress) || undefined;
  const phase = progress?.phase ?? 'requesting';
  const complete = phase === 'ready';
  const failed = phase === 'error';
  const waiting = isOperationProgressWaitingState(progress?.state);

  return {
    title: 'Loading Quick Fill',
    message: progress?.message || 'Loading Quick Fill data from Revit',
    detail: waiting && progress?.waitReason
      ? progress.waitReason
      : progress?.detail || 'Waiting for the Revit connector',
    status: failed ? 'error' : complete ? 'complete' : waiting ? 'waiting' : 'running',
    operationId: progress?.operationId,
    percent: failed ? null : complete ? 100 : waiting ? null : percent,
    phase,
    state: progress?.state,
    waitReason: progress?.waitReason,
    waitElapsedMs: progress?.waitElapsedMs,
    activeElapsedMs: progress?.activeElapsedMs,
    currentItem: progress?.currentItem,
    processed: progress?.processed,
    total: progress?.total,
    elapsedMs: progress?.elapsedMs ?? undefined,
    etaMs: waiting ? undefined : progress?.etaMs ?? undefined,
    cacheStatus: progress?.cacheStatus,
    resultReady: progress?.resultReady,
    stepLabel,
    locked: !complete && !failed,
    metrics: progress?.metrics,
  };
}
